import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const statusLabels = {
  open: { label: 'Åpen', badge: 'apen' },
  closed: { label: 'Fullført', badge: 'fullfort' },
  escalated: { label: 'Eskalert', badge: 'eskalert' },
  archived: { label: 'Arkivert', badge: 'ukjent' },
};

const escapeAttributeValue = (value) => {
  if (typeof value !== 'string') return '';
  if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return value.replace(/["\\]/g, '\\$&');
};

const looksLikeUrl = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return /^https?:\/\/\S+$/i.test(trimmed) || trimmed.startsWith('data:image/');
};

const extractImageUrls = (payload) => {
  const urls = new Set();

  const visit = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((item) => visit(item));
      return;
    }
    if (typeof value === 'object') {
      if (value === null) return;
      if (looksLikeUrl(value.url)) urls.add(value.url.trim());
      if (looksLikeUrl(value.href)) urls.add(value.href.trim());
      if (looksLikeUrl(value.src)) urls.add(value.src.trim());
      if (looksLikeUrl(value.original)) urls.add(value.original.trim());
      if (Array.isArray(value.urls)) value.urls.forEach((url) => visit(url));
      if (Array.isArray(value.files)) value.files.forEach((file) => visit(file));
      if (Array.isArray(value.attachments)) value.attachments.forEach((item) => visit(item));
      return;
    }
    if (looksLikeUrl(value)) {
      urls.add(value.trim());
    }
  };

  visit(payload);
  return Array.from(urls);
};

const normalizeMessageContent = (raw) => {
  if (!raw) {
    return { text: '', images: [] };
  }

  if (typeof raw !== 'string') {
    return { text: String(raw), images: extractImageUrls(raw) };
  }

  const trimmed = raw.trim();
  let parsed;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      parsed = null;
    }
  }

  if (parsed && typeof parsed === 'object') {
    const images = extractImageUrls(parsed);
    if (Array.isArray(parsed.files) && parsed.files.length > 0) {
      const names = parsed.files
        .map((file) => file?.name)
        .filter(Boolean)
        .join(', ');
      return {
        text: names || 'Filopplasting',
        images: images.length ? images : extractImageUrls(parsed.files),
      };
    }
    if (parsed.event === 'file_upload') {
      return {
        text: 'Filopplasting',
        images,
      };
    }
    return {
      text: raw,
      images,
    };
  }

  const images = looksLikeUrl(trimmed) ? [trimmed] : [];
  return { text: raw, images };
};

const formatDate = (value) =>
  new Date(value).toLocaleString('no-NO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatMessageTimestamp = (value) =>
  new Date(value).toLocaleString('no-NO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const ChatHistoryPage = () => {
  const { selectedCompany, permissions } = useAuth();
  const companyId = selectedCompany?.id ?? null;
  const location = useLocation();
  const [selectedBot, setSelectedBot] = useState('alle');
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, satisfaction: null });
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChatId, setExpandedChatId] = useState(null);
  const [transcripts, setTranscripts] = useState({});
  const [transcriptErrors, setTranscriptErrors] = useState({});
  const [loadingTranscriptId, setLoadingTranscriptId] = useState(null);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const targetChatId = queryParams.get('chatId');
  const [pendingChatId, setPendingChatId] = useState(targetChatId);

  const canView = Boolean(permissions?.canViewChatHistory);

  useEffect(() => {
    setPendingChatId(targetChatId);
  }, [targetChatId]);

  useEffect(() => {
    if (!canView || !companyId) {
      setBots([]);
      return undefined;
    }

    let isMounted = true;
    async function loadBots() {
      try {
        const response = await DashboardApi.getBots(null, companyId);
        if (!isMounted) return;
        const list = Array.isArray(response?.bots)
          ? response.bots
          : Array.isArray(response)
            ? response
            : [];
        setBots(
          list.map((bot) => ({
            id: bot.id,
            name: bot.display_title || bot.name,
          })),
        );
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || 'Kunne ikke hente botliste.');
      }
    }
    loadBots();
    return () => {
      isMounted = false;
    };
  }, [canView, companyId]);

  useEffect(() => {
    if (!canView || !companyId) {
      setConversations([]);
      setStats({ total: 0, open: 0, satisfaction: null });
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    async function loadHistory() {
      setLoading(true);
      try {
        const response = await DashboardApi.getChatHistory(
          companyId,
          {
            status: statusFilter,
            botId: selectedBot,
            keyword,
            from: fromDate,
            to: toDate,
          },
          controller.signal,
        );
        if (!isMounted) return;
        setConversations(response.conversations || []);
        setStats({
          total: response.stats?.total || 0,
          open: response.stats?.open || 0,
          satisfaction:
            response.stats?.satisfaction !== null && response.stats?.satisfaction !== undefined
              ? response.stats.satisfaction
              : null,
        });
        setError(null);
        setExpandedChatId(null);
        setTranscripts({});
        setTranscriptErrors({});
      } catch (err) {
        if (!isMounted || err.name === 'AbortError') return;
        setError(err.message || 'Kunne ikke hente chat-historikk.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadHistory();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [canView, companyId, selectedBot, statusFilter, keyword, fromDate, toDate]);

  const statuses = useMemo(
    () => [
      { value: 'open', label: 'Åpen' },
      { value: 'closed', label: 'Fullført' },
      { value: 'escalated', label: 'Eskalert' },
      { value: 'archived', label: 'Arkivert' },
    ],
    [],
  );

  const displayRows = useMemo(
    () =>
      conversations.map((conversation) => ({
        ...conversation,
        statusLabel: statusLabels[conversation.status]?.label || conversation.status,
        statusClass: statusLabels[conversation.status]?.badge || 'ukjent',
        satisfaction: conversation.satisfactionScore
          ? `${Number(conversation.satisfactionScore).toFixed(1)}/5`
          : '-',
        updatedFormatted: conversation.lastActivity ? formatDate(conversation.lastActivity) : '',
      })),
    [conversations],
  );

  const handleToggleConversation = useCallback(
    async (chatId) => {
      if (expandedChatId === chatId) {
        setExpandedChatId(null);
        return;
      }

      setExpandedChatId(chatId);

      if (transcripts[chatId] || loadingTranscriptId === chatId) {
        return;
      }

      setLoadingTranscriptId(chatId);
      setTranscriptErrors((prev) => {
        const next = { ...prev };
        delete next[chatId];
        return next;
      });

      try {
        const messages = await DashboardApi.getChatMessages(chatId);
        const normalized = Array.isArray(messages)
          ? messages.map((row) => {
              const rawType = row.sender_type || row.senderType || null;
              const normalizedType = typeof rawType === 'string' ? rawType.trim().toLowerCase() : null;
              let direction;
              switch (normalizedType) {
                case 'customer':
                case 'kunde':
                  direction = 'customer';
                  break;
                case 'bot':
                  direction = 'bot';
                  break;
                case 'agent':
                  direction = 'agent';
                  break;
                default:
                  direction = row.user_id !== null && row.user_id !== undefined ? 'customer' : 'bot';
                  break;
              }

              const { text, images } = normalizeMessageContent(row.message_content || '');

              return {
                message: text,
                images,
                createdAt: row.created_at,
                direction,
              };
            })
          : [];
        setTranscripts((prev) => ({ ...prev, [chatId]: normalized }));
      } catch (err) {
        if (err.status === 404) {
          setTranscripts((prev) => ({ ...prev, [chatId]: [] }));
        } else {
          setTranscriptErrors((prev) => ({
            ...prev,
            [chatId]: err.message || 'Kunne ikke hente chat-logg.',
          }));
        }
      } finally {
        setLoadingTranscriptId(null);
      }
    },
    [expandedChatId, loadingTranscriptId, transcripts],
  );

  useEffect(() => {
    if (!pendingChatId) return;
    const exists = conversations.find((conversation) => conversation.chatId === pendingChatId);
    if (!exists) return;
    handleToggleConversation(pendingChatId);
    const selector = `[data-chat-row="${escapeAttributeValue(pendingChatId)}"]`;
    const row = document.querySelector(selector);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('flash-highlight');
      setTimeout(() => row.classList.remove('flash-highlight'), 1600);
    }
    setPendingChatId(null);
  }, [conversations, pendingChatId, handleToggleConversation]); 

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Chat-historikk</h1>
          <p className="page-subtitle">
            Søk i samtaler, analyser kvaliteten og følg opp henvendelser i sanntid.
          </p>
        </div>
      </div>

      <section className="card-grid">
        <article className="card kpi-card">
          <header className="card-header">
            <span className="card-title">Samtaler i visning</span>
          </header>
          <p className="card-value">{stats.total}</p>
        </article>
        <article className="card kpi-card">
          <header className="card-header">
            <span className="card-title">Åpne henvendelser</span>
          </header>
          <p className="card-value">{stats.open}</p>
        </article>
      </section>

      <article className="card filter-card">
        <form className="filter-row">
          <div className="filter-field">
            <label htmlFor="filter-bot">Bot</label>
            <select
              id="filter-bot"
              className="select select--sm"
              value={selectedBot}
              onChange={(event) => setSelectedBot(event.target.value)}
            >
              <option value="alle">Alle bots</option>
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="filter-status">Status</label>
            <select
              id="filter-status"
              className="select select--sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="alle">Alle statuser</option>
              {statuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field filter-field--grow">
            <label htmlFor="filter-keyword">Søk</label>
            <input
              id="filter-keyword"
              className="input input--sm"
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="Kunde, emne eller tagg"
            />
          </div>
          <div className="filter-field">
            <label htmlFor="filter-from">Fra</label>
            <input
              id="filter-from"
              className="input input--sm"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>
          <div className="filter-field">
            <label htmlFor="filter-to">Til</label>
            <input
              id="filter-to"
              className="input input--sm"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>
        </form>
      </article>

      <article className="card conversations-card">
        <header className="card-header">
          <div>
            <h2>Interaksjoner</h2>
            <p className="card-subtitle">{displayRows.length} treff</p>
          </div>
        </header>
        <div className="conversation-list">
          <div className="conversation-list-header">
            <span className="col-subject">Emne</span>
            <span className="col-id">Chat-ID</span>
            <span className="col-status">Status</span>
            <span className="col-date">Oppdatert</span>
            <span className="col-action"></span>
          </div>
              {displayRows.map((conversation) => {
                const expanded = expandedChatId === conversation.chatId;
                const transcript = transcripts[conversation.chatId];
                const transcriptError = transcriptErrors[conversation.chatId];
                const loadingTranscript = loadingTranscriptId === conversation.chatId;
            const shortId = conversation.chatId.substring(0, 8) + '…';

                return (
              <div
                key={conversation.chatId}
                className={`conversation-item ${expanded ? 'is-expanded' : ''}`}
                      data-chat-row={conversation.chatId}
              >
                <div
                  className="conversation-header"
                      onClick={() => handleToggleConversation(conversation.chatId)}
                      role="button"
                      aria-expanded={expanded}
                    >
                  <span className="col-subject">
                        <strong>{conversation.subject}</strong>
                    {conversation.tags.length > 0 && (
                      <span className="conversation-tags">
                          {conversation.tags.map((tag) => (
                          <span key={tag} className="tag tag-light">{tag}</span>
                        ))}
                      </span>
                    )}
                  </span>
                  <span className="col-id">
                    <code className="conversation-id" title={conversation.chatId}>
                      {expanded ? conversation.chatId : shortId}
                    </code>
                            </span>
                  <span className="col-status">
                        <span className={`status-badge status-${conversation.statusClass}`}>
                          {conversation.statusLabel}
                        </span>
                  </span>
                  <span className="col-date">{conversation.updatedFormatted}</span>
                  <span className="col-action">
                    <span className="conversation-chevron">{expanded ? '▾' : '▸'}</span>
                  </span>
                </div>
                    {expanded && (
                  <div className="conversation-detail">
                          {loadingTranscript ? (
                      <p className="conversation-loading">Laster chat…</p>
                          ) : transcriptError ? (
                      <p className="conversation-error">⚠️ {transcriptError}</p>
                          ) : transcript && transcript.length > 0 ? (
                            <div className="chat-transcript">
                              {transcript.map((message, index) => (
                                <div
                                  key={`${conversation.chatId}-${index}`}
                                  className={`chat-transcript-row ${message.direction}`}
                                >
                                  <span className="chat-transcript-meta">
                                    {message.direction === 'customer'
                                      ? 'Kunde'
                                      : message.direction === 'agent'
                                        ? 'Agent'
                                        : 'Bot'}{' '}
                                    · {formatMessageTimestamp(message.createdAt)}
                                  </span>
                                  <div className="chat-transcript-bubble">
                                    {message.message && (
                                      <span className="chat-transcript-text">{message.message}</span>
                                    )}
                                    {Array.isArray(message.images) && message.images.length > 0 && (
                                      <div className="chat-transcript-images">
                                        {message.images.map((url) => (
                                          <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                            <img src={url} alt="Opplastet bilde" />
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                      <p className="conversation-empty">Ingen meldinger i denne chatten ennå.</p>
                    )}
                  </div>
                )}
              </div>
                );
              })}
          {(displayRows.length === 0 || error) && (
            <div className="empty-state">
              {error ? (
                <p>⚠️ {error}</p>
              ) : loading ? (
                <p>Laster samtaler…</p>
              ) : (
                <p>Ingen treff for valgte filtre. Juster filtrene for å utvide søket.</p>
              )}
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default ChatHistoryPage;
